import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../../../contexts/AuthContext';
import PointApprovalList from './PointApprovalList';
import SalaryTaxSettingsPanel from './SalaryTaxSettingsPanel';

interface PointApprovalManagementProps {
  period?: 'daily' | 'weekly' | 'monthly' | 'tax';
}

const PointApprovalManagement: React.FC<PointApprovalManagementProps> = ({ period }) => {
  useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [tabValue, setTabValue] = useState(1); // 0: daily, 1: weekly, 2: monthly, 3: tax
  const [deadlineMessage, setDeadlineMessage] = useState('');
  const [salaryTaxSettingsRevision, setSalaryTaxSettingsRevision] = useState(0);

  const getDeadlineMessage = (tv: number): string => {
    if (tv === 3) {
      return '';
    }
    const now = new Date();
    let deadlineDate: Date;
    let message: string;

    switch (tv) {
      case 0:
        deadlineDate = new Date(now);
        deadlineDate.setDate(now.getDate() + 1);
        deadlineDate.setHours(23, 59, 0, 0);
        message = `매일 23:59에 포인트가 갱신됩니다. 오늘 포인트 지급 마감 시간: ${deadlineDate.toLocaleString()}`;
        break;
      case 1:
        const daysUntilSunday = 7 - now.getDay();
        deadlineDate = new Date(now);
        deadlineDate.setDate(now.getDate() + daysUntilSunday);
        deadlineDate.setHours(23, 59, 0, 0);
        message = `매주 일요일 23:59에 포인트가 갱신됩니다. 이번 주 포인트 지급 마감 시간: ${deadlineDate.toLocaleString()}`;
        break;
      case 2:
        deadlineDate = new Date(now.getFullYear(), now.getMonth() + 1, 1);
        deadlineDate.setHours(0, 1, 0, 0);
        message = `매월 1일 오전 00:01:00에 포인트가 갱신됩니다. 다음 포인트 지급 시간은 ${deadlineDate.toLocaleString()}입니다.`;
        break;
      default:
        deadlineDate = new Date(now);
        deadlineDate.setDate(now.getDate() + 1);
        deadlineDate.setHours(23, 59, 0, 0);
        message = `매일 23:59에 포인트가 갱신됩니다. 오늘 포인트 지급 마감 시간: ${deadlineDate.toLocaleString()}`;
    }
    return message;
  };

  useEffect(() => {
    if (period) {
      const periodToTabValue =
        period === 'daily' ? 0 : period === 'weekly' ? 1 : period === 'monthly' ? 2 : 3;
      setDeadlineMessage(getDeadlineMessage(periodToTabValue));
    } else {
      setDeadlineMessage(getDeadlineMessage(tabValue));
    }
  }, [tabValue, period]);

  const handleTabChange = (newValue: number) => {
    setTabValue(newValue);
    switch (newValue) {
      case 0:
        navigate('/class/teacher/point-approval/daily');
        break;
      case 1:
        navigate('/class/teacher/point-approval/weekly');
        break;
      case 2:
        navigate('/class/teacher/point-approval/monthly');
        break;
      case 3:
        navigate('/class/teacher/point-approval/tax');
        break;
      default:
        break;
    }
  };

  const getPeriodFromTabValue = (tabValue: number): "daily" | "weekly" | "monthly" => {
    switch (tabValue) {
      case 0:
        return "daily";
      case 1:
        return "weekly";
      case 2:
        return "monthly";
      default:
        return "weekly";
    }
  };

  // period prop 또는 URL 경로에 따라 탭 값 설정
  useEffect(() => {
    if (period) {
      switch (period) {
        case 'daily':
          setTabValue(0);
          break;
        case 'weekly':
          setTabValue(1);
          break;
        case 'monthly':
          setTabValue(2);
          break;
        case 'tax':
          setTabValue(3);
          break;
      }
    } else {
      const path = location.pathname;
      if (path.includes('/point-approval/daily')) {
        setTabValue(0);
      } else if (path.includes('/point-approval/weekly')) {
        setTabValue(1);
      } else if (path.includes('/point-approval/monthly')) {
        setTabValue(2);
      } else if (path.includes('/point-approval/tax')) {
        setTabValue(3);
      } else {
        setTabValue(1);
      }
    }
  }, [period, location.pathname]);

  const pxToRem = (value: number) => `${value / 16}rem`;

  return (
    <div style={{
      width: '100%',
      minHeight: 'fit-content',
      display: 'flex',
      flexDirection: 'column',
      gap: pxToRem(32)
    }}>
      {/* 헤더 */}
      <div style={{
        alignSelf: 'stretch',
        justifyContent: 'center',
        display: 'flex',
        flexDirection: 'column',
        color: 'var(--Color-text-normal, #161616)',
        fontSize: pxToRem(32),
        fontFamily: 'Pretendard, sans-serif',
        fontWeight: '600',
        lineHeight: pxToRem(48),
        wordWrap: 'break-word'
      }}>
        포인트 관리
      </div>

      {/* 탭: 일/주/월 승인 + 급여 세금(공제) */}
      <div style={{
        alignSelf: 'stretch',
        flexDirection: 'column',
        justifyContent: 'flex-start',
        alignItems: 'flex-start',
        gap: pxToRem(20),
        display: 'flex'
      }}>
        <div style={{
          alignSelf: 'stretch',
          justifyContent: 'flex-start',
          alignItems: 'center',
          gap: pxToRem(16),
          display: 'flex',
          flexWrap: 'wrap',
          rowGap: pxToRem(12),
        }}>
          <div
            onClick={() => handleTabChange(0)}
            style={{
              maxHeight: pxToRem(40),
              minHeight: pxToRem(40),
              paddingLeft: pxToRem(12),
              paddingRight: pxToRem(12),
              paddingTop: pxToRem(8),
              paddingBottom: pxToRem(8),
              background: tabValue === 0 ? 'var(--Color-fill-brand-color, #FF6F0F)' : 'transparent',
              borderRadius: pxToRem(8),
              justifyContent: 'center',
              alignItems: 'center',
              gap: pxToRem(4),
              display: 'flex',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
          >
            <div style={{
              color: tabValue === 0 ? 'var(--Color-text-inverse, white)' : 'var(--Color-text-normal, #161616)',
              fontSize: pxToRem(14),
              fontFamily: 'Pretendard, sans-serif',
              fontWeight: '500',
              lineHeight: pxToRem(21),
              wordWrap: 'break-word'
            }}>
              일별 포인트 승인
            </div>
          </div>
          <div
            onClick={() => handleTabChange(1)}
            style={{
              maxHeight: pxToRem(40),
              minHeight: pxToRem(40),
              paddingLeft: pxToRem(12),
              paddingRight: pxToRem(12),
              paddingTop: pxToRem(8),
              paddingBottom: pxToRem(8),
              background: tabValue === 1 ? 'var(--Color-fill-brand-color, #FF6F0F)' : 'transparent',
              borderRadius: pxToRem(8),
              justifyContent: 'center',
              alignItems: 'center',
              gap: pxToRem(4),
              display: 'flex',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
          >
            <div style={{
              color: tabValue === 1 ? 'var(--Color-text-inverse, white)' : 'var(--Color-text-normal, #161616)',
              fontSize: pxToRem(14),
              fontFamily: 'Pretendard, sans-serif',
              fontWeight: '500',
              lineHeight: pxToRem(21),
              wordWrap: 'break-word'
            }}>
              주별 포인트 승인
            </div>
          </div>
          <div
            onClick={() => handleTabChange(2)}
            style={{
              maxHeight: pxToRem(40),
              minHeight: pxToRem(40),
              paddingLeft: pxToRem(12),
              paddingRight: pxToRem(12),
              paddingTop: pxToRem(8),
              paddingBottom: pxToRem(8),
              background: tabValue === 2 ? 'var(--Color-fill-brand-color, #FF6F0F)' : 'transparent',
              borderRadius: pxToRem(8),
              justifyContent: 'center',
              alignItems: 'center',
              gap: pxToRem(4),
              display: 'flex',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
          >
            <div style={{
              color: tabValue === 2 ? 'var(--Color-text-inverse, white)' : 'var(--Color-text-normal, #161616)',
              fontSize: pxToRem(14),
              fontFamily: 'Pretendard, sans-serif',
              fontWeight: '500',
              lineHeight: pxToRem(21),
              wordWrap: 'break-word'
            }}>
              월별 포인트 승인
            </div>
          </div>
          <div
            onClick={() => handleTabChange(3)}
            style={{
              maxHeight: pxToRem(40),
              minHeight: pxToRem(40),
              paddingLeft: pxToRem(12),
              paddingRight: pxToRem(12),
              paddingTop: pxToRem(8),
              paddingBottom: pxToRem(8),
              background: tabValue === 3 ? 'var(--Color-fill-brand-color, #FF6F0F)' : 'transparent',
              borderRadius: pxToRem(8),
              justifyContent: 'center',
              alignItems: 'center',
              gap: pxToRem(4),
              display: 'flex',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
          >
            <div style={{
              color: tabValue === 3 ? 'var(--Color-text-inverse, white)' : 'var(--Color-text-normal, #161616)',
              fontSize: pxToRem(14),
              fontFamily: 'Pretendard, sans-serif',
              fontWeight: '500',
              lineHeight: pxToRem(21),
              wordWrap: 'break-word'
            }}>
              급여 세금(공제)
            </div>
          </div>
        </div>

        {tabValue === 3 ? (
          <SalaryTaxSettingsPanel
            onSaved={() => setSalaryTaxSettingsRevision((n) => n + 1)}
          />
        ) : (
          <PointApprovalList
            period={getPeriodFromTabValue(tabValue)}
            deadlineMessage={deadlineMessage}
            salaryTaxSettingsRevision={salaryTaxSettingsRevision}
          />
        )}
      </div>
    </div>
  );
};

export default PointApprovalManagement;
